import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useAddressValidation, usePlaceValidator } from '../../../Form/fields/PlaceField'
import { type AddressesFormValues } from './AddressesForm.type'
import { type Move } from '../../../../modules/moves'

type UseValidationSchemaOptions = {
  move: Move
  specificAddresses?: boolean
}

const useValidationSchema = ({
  move,
  specificAddresses = false,
}: UseValidationSchemaOptions) => {
  const { t } = useTranslation()
  const placeValidator = usePlaceValidator()
  const addressValidation = useAddressValidation()

  const hasOrigin = !!move.locations.origin?.id
  const hasDestination = !!move.locations.destination?.id
  const hasWarehouse = !!move.locations.warehouse?.id
  const hasStops = !!move.locations.stops?.length

  const placeShape: Yup.ObjectShape = {
    address: specificAddresses ? addressValidation : placeValidator,
    nbStairs: Yup.number().min(0).max(9),
  }
  if (specificAddresses) {
    placeShape.elevatorReservationRequired = Yup.string().required()
  }

  const stopShape: Yup.ObjectShape = {
    address: specificAddresses ? addressValidation : placeValidator,
    nbStairs: Yup.number().min(0).max(9),
    description: Yup.string().required(),
    stopDurationHours: Yup.string().matches(/^([0-9](\.[0-9]{1,2})?)$/, t('formValidation.defaultShort')),
  }
  if (specificAddresses) {
    placeShape.elevatorReservationRequired = Yup.string().required()
  }

  const shape: Yup.ObjectShape = {}
  if (hasOrigin) {
    shape.origin = Yup.object(placeShape)
  }
  if (hasDestination) {
    shape.destination = Yup.object(placeShape)
  }
  if (hasWarehouse) {
    shape.warehouse = Yup.object(placeShape)
  }
  if (hasStops) {
    shape.stops = Yup.array().of(Yup.object(stopShape))
  }

  return Yup.object().shape({
    locations: Yup.object().shape(shape),
  }) as Yup.ObjectSchema<AddressesFormValues, AddressesFormValues>
}

export default useValidationSchema
